import React, { useEffect, useRef, useState } from "react";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import AuthImage from "./AuthImage";
import { Plus, AirplaneTilt, Buildings, MapPin, Camera, Image as ImageIcon, X } from "@phosphor-icons/react";

const CATS = [
    { v: "destination", label: "Destination", icon: MapPin },
    { v: "flight", label: "Flight", icon: AirplaneTilt },
    { v: "hotel", label: "Hotel", icon: Buildings },
    { v: "activity", label: "Activity", icon: Camera },
];

const ALLOWED = ["image/jpeg", "image/png", "image/gif", "image/webp"];
const MAX_BYTES = 5 * 1024 * 1024;

export default function CreatePostDialog({
    onCreated,
    onUpdated,
    trigger,
    editingPost = null,
    open: controlledOpen,
    onOpenChange: controlledOnOpenChange,
}) {
    const isEdit = !!editingPost;
    const [internalOpen, setInternalOpen] = useState(false);
    const open = controlledOpen ?? internalOpen;
    const setOpen = controlledOnOpenChange ?? setInternalOpen;

    const [category, setCategory] = useState("destination");
    const [title, setTitle] = useState("");
    const [city, setCity] = useState("");
    const [description, setDescription] = useState("");
    const [url, setUrl] = useState("");
    const [tags, setTags] = useState("");
    const [imageUrl, setImageUrl] = useState("");
    const [imagePreview, setImagePreview] = useState("");
    const [uploading, setUploading] = useState(false);
    const [loading, setLoading] = useState(false);
    const fileRef = useRef(null);

    const reset = () => {
        setCategory("destination"); setTitle(""); setCity(""); setDescription(""); setUrl(""); setTags("");
        setImageUrl(""); setImagePreview("");
    };

    // Prefill fields when entering edit mode
    useEffect(() => {
        if (!open) return;
        if (isEdit) {
            setCategory(editingPost.category || "destination");
            setTitle(editingPost.title || "");
            setCity(editingPost.city || "");
            setDescription(editingPost.description || "");
            setUrl(editingPost.url || "");
            setTags((editingPost.tags || []).join(", "));
            setImageUrl(editingPost.image_url || "");
            setImagePreview(""); // we don't have a local blob; show url-mode hint below
        } else {
            reset();
        }
    }, [open, isEdit, editingPost]);

    const onFile = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (!ALLOWED.includes(file.type)) {
            toast.error("Only JPG, PNG, GIF, or WebP images allowed.");
            return;
        }
        if (file.size > MAX_BYTES) {
            toast.error("Image too large (max 5 MB).");
            return;
        }
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/upload", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setImageUrl(data.url);
            setImagePreview(URL.createObjectURL(file));
            toast.success("Image uploaded");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
        } finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    const removeImage = () => {
        setImageUrl(""); setImagePreview("");
    };

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const payload = {
                category, title, city, description,
                url: url || "",
                image_url: imageUrl || "",
                tags: tags.split(",").map((t) => t.trim()).filter(Boolean),
            };
            if (isEdit) {
                const { data } = await api.put(`/posts/${editingPost.id}`, payload);
                toast.success("Post updated");
                setOpen(false);
                onUpdated?.(data);
            } else {
                const { data } = await api.post("/posts", payload);
                toast.success("Posted! Fight on.");
                reset();
                setOpen(false);
                onCreated?.(data);
            }
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || (isEdit ? "Failed to update" : "Failed to post"));
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            {!isEdit && (
                <DialogTrigger asChild>
                    {trigger || (
                        <Button data-testid="open-create-post-dialog-btn" className="bg-[#990000] hover:bg-[#7A0000] text-white h-10">
                            <Plus size={16} weight="bold" className="mr-1.5" />
                            Share a tip
                        </Button>
                    )}
                </DialogTrigger>
            )}
            <DialogContent className="sm:max-w-[560px] max-h-[90vh] overflow-y-auto" data-testid={isEdit ? "edit-post-dialog" : "create-post-dialog"}>
                <DialogHeader>
                    <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#990000] mb-1">
                        {isEdit ? "Edit Recommendation" : "New Recommendation"}
                    </div>
                    <DialogTitle className="font-heading text-2xl font-semibold text-[#0A0A0A]">
                        {isEdit ? "Edit your travel tip" : "Share a travel tip"}
                    </DialogTitle>
                    <DialogDescription className="text-stone-500">
                        {isEdit
                            ? "Update your post — fellow Trojans will see the latest details."
                            : "Help fellow Trojans plan their India trip with your real-world recommendation."}
                    </DialogDescription>
                </DialogHeader>

                <form onSubmit={submit} className="space-y-4 pt-2">
                    <div>
                        <Label className="text-xs uppercase tracking-[0.15em] font-bold text-stone-600">Category</Label>
                        <Select value={category} onValueChange={setCategory}>
                            <SelectTrigger data-testid="create-post-category-select" className="mt-2 h-11 border-stone-300">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {CATS.map((c) => (
                                    <SelectItem key={c.v} value={c.v} data-testid={`category-option-${c.v}`}>
                                        <div className="flex items-center gap-2">
                                            <c.icon size={16} className="text-[#990000]" />
                                            {c.label}
                                        </div>
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div>
                        <Label htmlFor="title" className="text-xs uppercase tracking-[0.15em] font-bold text-stone-600">Title</Label>
                        <Input id="title" data-testid="create-post-title-input" value={title} onChange={(e) => setTitle(e.target.value)}
                               placeholder="e.g., Best 3-day Goa itinerary" maxLength={160}
                               className="mt-2 h-11 border-stone-300 focus-visible:ring-[#990000] focus-visible:border-[#990000]" required />
                    </div>

                    <div>
                        <Label htmlFor="city" className="text-xs uppercase tracking-[0.15em] font-bold text-stone-600">City / Region</Label>
                        <Input id="city" data-testid="create-post-city-input" value={city} onChange={(e) => setCity(e.target.value)}
                               placeholder="e.g., Goa, Bengaluru, Kerala"
                               className="mt-2 h-11 border-stone-300 focus-visible:ring-[#990000] focus-visible:border-[#990000]" required />
                    </div>

                    <div>
                        <Label htmlFor="description" className="text-xs uppercase tracking-[0.15em] font-bold text-stone-600">Description</Label>
                        <Textarea id="description" data-testid="create-post-description-input" value={description} onChange={(e) => setDescription(e.target.value)}
                                  placeholder="Share details, prices, tips, what to expect…" rows={5}
                                  className="mt-2 border-stone-300 focus-visible:ring-[#990000] focus-visible:border-[#990000]" required />
                    </div>

                    <div>
                        <Label className="text-xs uppercase tracking-[0.15em] font-bold text-stone-600">
                            Photo <span className="font-normal lowercase tracking-normal text-stone-400">(optional, max 5 MB)</span>
                        </Label>
                        {imagePreview ? (
                            <div className="mt-2 relative rounded-lg overflow-hidden border border-stone-200">
                                <img src={imagePreview} alt="" className="w-full h-48 object-cover" data-testid="create-post-image-preview" />
                                <button
                                    type="button"
                                    onClick={removeImage}
                                    data-testid="create-post-remove-image-btn"
                                    className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white/95 border border-stone-200 hover:border-[#990000] hover:text-[#990000] flex items-center justify-center"
                                >
                                    <X size={14} weight="bold" />
                                </button>
                            </div>
                        ) : imageUrl ? (
                            <div className="mt-2 relative rounded-lg overflow-hidden border border-stone-200">
                                <AuthImage src={imageUrl} alt="" className="w-full h-48 object-cover" testId="edit-post-existing-image" />
                                <button
                                    type="button"
                                    onClick={removeImage}
                                    data-testid="edit-post-remove-image-btn"
                                    className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white/95 border border-stone-200 hover:border-[#990000] hover:text-[#990000] flex items-center justify-center"
                                >
                                    <X size={14} weight="bold" />
                                </button>
                            </div>
                        ) : (
                            <label
                                data-testid="create-post-upload-label"
                                className={`mt-2 flex flex-col items-center justify-center gap-2 h-32 border-2 border-dashed border-stone-300 rounded-lg cursor-pointer hover:border-[#990000] hover:bg-[#990000]/5 transition-colors ${uploading ? "opacity-60 pointer-events-none" : ""}`}
                            >
                                <input
                                    ref={fileRef}
                                    data-testid="create-post-image-input"
                                    type="file"
                                    accept="image/jpeg,image/png,image/gif,image/webp"
                                    onChange={onFile}
                                    className="hidden"
                                />
                                <ImageIcon size={22} className="text-stone-400" />
                                <div className="text-sm text-stone-500">
                                    {uploading ? "Uploading…" : "Click to upload an image"}
                                </div>
                                <div className="text-[10px] uppercase tracking-wider text-stone-400 font-bold">
                                    JPG · PNG · GIF · WebP
                                </div>
                            </label>
                        )}
                    </div>

                    <div>
                        <Label htmlFor="url" className="text-xs uppercase tracking-[0.15em] font-bold text-stone-600">URL <span className="font-normal lowercase tracking-normal text-stone-400">(optional)</span></Label>
                        <Input id="url" type="url" data-testid="create-post-url-input" value={url} onChange={(e) => setUrl(e.target.value)}
                               placeholder="https://booking-link-or-blog.com"
                               className="mt-2 h-11 border-stone-300 focus-visible:ring-[#990000] focus-visible:border-[#990000]" />
                    </div>

                    <div>
                        <Label htmlFor="tags" className="text-xs uppercase tracking-[0.15em] font-bold text-stone-600">Tags <span className="font-normal lowercase tracking-normal text-stone-400">(comma-separated)</span></Label>
                        <Input id="tags" data-testid="create-post-tags-input" value={tags} onChange={(e) => setTags(e.target.value)}
                               placeholder="beach, budget, family"
                               className="mt-2 h-11 border-stone-300 focus-visible:ring-[#990000] focus-visible:border-[#990000]" />
                    </div>

                    <DialogFooter className="pt-2">
                        <Button type="button" variant="ghost" onClick={() => setOpen(false)} data-testid={isEdit ? "edit-post-cancel-btn" : "create-post-cancel-btn"}>
                            Cancel
                        </Button>
                        <Button type="submit" disabled={loading || uploading} data-testid={isEdit ? "edit-post-submit-btn" : "create-post-submit-btn"}
                                className="bg-[#990000] hover:bg-[#7A0000] text-white">
                            {loading ? (isEdit ? "Saving…" : "Posting…") : (isEdit ? "Save changes" : "Post")}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
